/**
 * Creep / pressure model (§1.4). Every frontier hex compares the strongest non-allied
 * attacker's pressure with its defence; progress accumulates and the hex flips at 1.
 * Attacker pressure is scaled by supply lines (distance to the attacker's capital, §1.12) and
 * halved across sea links (§1.12); capitals cannot be captured before 10:00 (§1.11). The
 * creep pass also fills each player's focused-front readout (§1.13).
 */
import { BALANCE, DT } from '../balance.js';
import { betrayalLocked, allied, capEff, inCeasefire, removeEntity, setCityLevel, bit } from '../entities.js';
import type { FrontStatus, MatchState, SimEvent } from '../types.js';
import { capitalCity, capitalResistsCapture } from './capital.js';

const C = BALANCE.creep;
const FR = BALANCE.front;

/** Supply factor of attacker p on hex h: clamp(1 − perMu·max(0, d − freeMu), min, 1). */
export function supplyFactor(d: number): number {
  return Math.max(C.supplyMin, Math.min(1, 1 - C.supplyPerMu * Math.max(0, d - C.supplyFreeMu)));
}

const supplyCache = new WeakMap<MatchState, Float32Array[]>();

/** Per player (index id), per land hex: the supply factor. Capitals never move, so computed once. */
function supplyTables(state: MatchState): Float32Array[] {
  let t = supplyCache.get(state);
  if (!t) {
    const w = state.world;
    t = [new Float32Array(0)];
    for (const p of state.players) {
      const f = new Float32Array(w.landCount);
      const cx = w.cx[p.capitalHex];
      const cy = w.cy[p.capitalHex];
      for (let h = 0; h < w.landCount; h++) f[h] = supplyFactor(Math.hypot(w.cx[h] - cx, w.cy[h] - cy));
      t.push(f);
    }
    supplyCache.set(state, t);
  }
  return t;
}

/** hostileMat[a * 9 + b]: player a may press into land owned by b (b = 0 → neutral). */
function hostilityMatrix(state: MatchState): Uint8Array {
  const m = new Uint8Array(81);
  for (const pa of state.players) {
    if (pa.eliminatedTick >= 0) continue;
    const a = pa.id;
    m[a * 9] = 1;
    for (const pb of state.players) {
      const b = pb.id;
      if (b !== a && !allied(state, a, b) && !betrayalLocked(state, a, b) && !inCeasefire(state, a, b)) m[a * 9 + b] = 1;
    }
  }
  return m;
}

/** Max capture progress per second against neutral land at `tick` (neutral resistance curve). */
export function neutralRateCap(tick: number, players = 8): number {
  const f = Math.min(1, Math.max(0, (tick - C.neutralRampFrom) / (C.neutralRampTo - C.neutralRampFrom)));
  return (C.neutralRateEarly + (C.neutralRateLate - C.neutralRateEarly) * f) * (BALANCE.regionSets[players]?.neutralRate ?? 1);
}

export function env(n: number): number {
  return Math.min(C.envMax, Math.max(C.envMin, 1 + C.envPerN * (n - C.envN0)));
}

export function creepSystem(state: MatchState, events: SimEvent[]): void {
  const w = state.world;
  const N = w.landCount;
  const { owner, pop, prog, progBy, rad } = state;
  const { nbr, linkStart, linkHex, linkW, q, r } = w;
  const tick = state.tick;
  const hostileMat = hostilityMatrix(state);
  const phase = tick >= BALANCE.phases.finalTick ? C.phaseFinal : 1;
  const home = tick < BALANCE.phases.homeLateTick ? C.homeEarly : C.homeLate;
  const neutralCap = neutralRateCap(tick, state.players.length);
  const hardenedCapitals = tick < BALANCE.phases.capitalsExposedTick;
  const supply = supplyTables(state);

  const np = state.players.length;
  const fq = new Int32Array(np + 1);
  const fr = new Int32Array(np + 1);
  const hasFocus = new Uint8Array(np + 1);
  const region = new Int32Array(np + 1);
  const capital = new Int32Array(np + 1).fill(-1);
  for (const p of state.players) {
    region[p.id] = p.regionId;
    if (p.eliminatedTick < 0) capital[p.id] = p.capitalHex;
    if (p.focusHex >= 0) {
      hasFocus[p.id] = 1;
      fq[p.id] = q[p.focusHex];
      fr[p.id] = r[p.focusHex];
    }
  }
  // focused-front readout accumulators: our attack / their defence in the zone, and the reverse
  const attSum = new Float64Array(np + 1);
  const defSum = new Float64Array(np + 1);
  const foeSum = new Float64Array(np + 1);
  const holdSum = new Float64Array(np + 1);

  const wSum = new Float64Array(9);
  const pSum = new Float64Array(9);
  const landN = new Float64Array(9);
  const flips: number[] = [];
  let contact = 0;

  for (let j = 0; j < N; j++) {
    const oj = owner[j];
    let touched = 0;
    const base = j * 6;
    for (let k = 0; k < 6; k++) {
      const n = nbr[base + k];
      if (n < 0) continue;
      const a = owner[n];
      if (a === 0 || a === oj || !hostileMat[a * 9 + oj]) continue;
      const b = 1 << a;
      if (!(touched & b)) {
        touched |= b;
        wSum[a] = 0;
        pSum[a] = 0;
        landN[a] = 0;
      }
      wSum[a] += 1;
      landN[a] += 1;
      pSum[a] += pop[n];
    }
    for (let l = linkStart[j]; l < linkStart[j + 1]; l++) {
      const n = linkHex[l];
      const a = owner[n];
      if (a === 0 || a === oj || !hostileMat[a * 9 + oj]) continue;
      const b = 1 << a;
      const lw = linkW[l];
      if (!(touched & b)) {
        touched |= b;
        wSum[a] = 0;
        pSum[a] = 0;
        landN[a] = 0;
      }
      wSum[a] += lw;
      pSum[a] += lw * pop[n];
    }

    if (!touched) {
      state.frontier[j] = 0;
      if (prog[j] > 0) prog[j] = Math.max(0, prog[j] - C.progDecay * DT);
      continue;
    }
    state.frontier[j] = 1;
    if (oj !== 0) contact |= touched | (1 << oj);

    // defence: mean pop over {j} ∪ own neighbours (sea links weighted)
    let dw = 1;
    let dp = pop[j];
    for (let k = 0; k < 6; k++) {
      const n = nbr[base + k];
      if (n >= 0 && owner[n] === oj) {
        dw += 1;
        dp += pop[n];
      }
    }
    for (let l = linkStart[j]; l < linkStart[j + 1]; l++) {
      const n = linkHex[l];
      if (owner[n] === oj) {
        dw += linkW[l];
        dp += linkW[l] * pop[n];
      }
    }
    let def = (oj === 0 ? C.resNeutral : 1) * C.defEdge * (dp / dw) * (1 - C.radDef * rad[j]);
    if (oj !== 0 && w.coreOf[j] !== 0 && w.coreOf[j] === region[oj]) def *= home;
    def *= 1 + C.cityPerLevel * state.cityLvl[j];
    if (tick - state.flipTick[j] < C.freshTicks) def *= C.freshMult;
    const defEff = Math.max(def, C.minDef);

    let best = 0;
    let bestA = 0;
    let bestFocused = false;
    let bestSupply = 1;
    for (let a = 1; a <= np; a++) {
      if (!(touched & (1 << a))) continue;
      const focused = hasFocus[a] === 1 && axialDistInt(q[j], r[j], fq[a], fr[a]) <= C.focusRadius;
      // supply lines × sea-link-only reach (also caps the neutral capture rate)
      const sup = supply[a][j] * (landN[a] === 0 ? C.seaOnlyMult : 1);
      const att = (focused ? C.focusIn : C.focusOut) * env(wSum[a]) * (pSum[a] / wSum[a]) * phase * sup;
      if (focused) {
        attSum[a] += att;
        defSum[a] += defEff;
      }
      if (att > best) {
        best = att;
        bestA = a;
        bestFocused = focused;
        bestSupply = sup;
      }
    }
    if (oj !== 0 && hasFocus[oj] === 1 && axialDistInt(q[j], r[j], fq[oj], fr[oj]) <= C.focusRadius) {
      foeSum[oj] += best;
      holdSum[oj] += defEff;
    }
    const ratio = best / defEff;
    if (ratio > 1) {
      if (progBy[j] !== bestA) {
        progBy[j] = bestA;
        prog[j] = 0;
      }
      const cap = (bestFocused ? neutralCap * C.neutralFocusBonus : neutralCap) * bestSupply;
      const rate = oj === 0 ? Math.min(C.progRate * Math.min(ratio - 1, C.neutralMaxExcess), cap) : C.progRate * Math.min(ratio - 1, C.progMaxExcess);
      prog[j] += rate * DT;
      // hardened capital: pressure shows, but the hex cannot fall before capitals are exposed
      if (hardenedCapitals && oj !== 0 && j === capital[oj] && prog[j] > MAX_HARDENED_PROG) prog[j] = MAX_HARDENED_PROG;
      // second life (§1.11): the first capture of an exposed capital makes it critical instead
      if (prog[j] >= 1 && oj !== 0 && j === capital[oj] && capitalResistsCapture(state, state.players[oj - 1], capitalCity(state, j), events)) prog[j] = 0;
      if (prog[j] >= 1) flips.push(j);
    } else if (prog[j] > 0) {
      prog[j] = Math.max(0, prog[j] - C.progDecay * DT);
    }
  }

  if (contact) {
    if (state.stats.firstContactTick === null) state.stats.firstContactTick = tick;
    for (let p = 1; p <= np; p++) if (contact & bit(p)) state.stats.players[p - 1].contactTicks++;
  }

  const net = new Float64Array(np + 1);
  for (const j of flips) {
    const to = progBy[j];
    const from = owner[j];
    if (hasFocus[to] && axialDistInt(q[j], r[j], fq[to], fr[to]) <= C.focusRadius) net[to]++;
    if (from && hasFocus[from] && axialDistInt(q[j], r[j], fq[from], fr[from]) <= C.focusRadius) net[from]--;
    flipHex(state, j, to, events);
  }
  updateFronts(state, attSum, defSum, foeSum, holdSum, net, hasFocus);
}

/** prog ceiling on a hardened capital hex (§1.11) */
const MAX_HARDENED_PROG = 0.99;
/** ratio readout ceiling (6-bit wire field, 0.1 steps) */
const MAX_FRONT_RATIO = 6.3;

/** Refresh every player's focused-front readout from this tick's creep pass (§1.13). */
function updateFronts(state: MatchState, attSum: Float64Array, defSum: Float64Array, foeSum: Float64Array, holdSum: Float64Array, net: Float64Array, hasFocus: Uint8Array): void {
  const k = 1 / FR.rateTicks;
  for (const p of state.players) {
    const f = p.front;
    if (!hasFocus[p.id] || p.eliminatedTick >= 0) {
      p.front = f.status === 'none' && f.ratio === 0 && f.rate === 0 ? f : { status: 'none', ratio: 0, rate: 0 };
      continue;
    }
    const rate = f.rate + (net[p.id] * 600 - f.rate) * k;
    const ratio = defSum[p.id] > 0 ? Math.min(MAX_FRONT_RATIO, attSum[p.id] / defSum[p.id]) : 0;
    const foe = holdSum[p.id] > 0 ? foeSum[p.id] / holdSum[p.id] : 0;
    let status: FrontStatus;
    if (defSum[p.id] === 0 && holdSum[p.id] === 0) status = 'none';
    else if (rate <= -FR.moveRate || (foe > 1 && foe > ratio) || (defSum[p.id] > 0 && ratio < FR.losing && rate < FR.moveRate)) status = 'losing';
    else if (ratio >= FR.advancing || rate >= FR.moveRate) status = 'advancing';
    else status = 'stalled';
    p.front = { status, ratio, rate };
  }
}

function axialDistInt(q1: number, r1: number, q2: number, r2: number): number {
  const dq = q1 - q2;
  const dr = r1 - r2;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) >> 1;
}

/** Hex j changes hands to `to` via creep (§1.4 "on prog ≥ 1"). */
export function flipHex(state: MatchState, j: number, to: number, events: SimEvent[]): void {
  const w = state.world;
  const from = state.owner[j];
  const pop = state.pop;
  pop[j] *= from === 0 ? C.survivorsNeutral : C.survivorsEnemy;
  for (let k = 0; k < 6; k++) {
    const n = w.nbr[j * 6 + k];
    if (n >= 0 && state.owner[n] === to) {
      const give = C.pourIn * pop[n];
      pop[n] -= give;
      pop[j] += give;
    }
  }
  state.owner[j] = to;
  state.prog[j] = 0;
  state.progBy[j] = 0;
  state.flipTick[j] = state.tick;
  const sid = state.structId[j];
  if (sid >= 0) {
    const e = state.entities.get(sid)!;
    if (e.kind === 'city' && e.buildDoneTick <= state.tick) {
      e.owner = to;
      e.upgradeDoneTick = 0;
      e.upgradeCost = 0;
      setCityLevel(state, e, Math.max(1, e.level - 1));
      if (from > 0) {
        state.sensorsDirty |= bit(from);
        state.stats.players[from - 1].citiesLost++;
      }
      state.sensorsDirty |= bit(to);
      events.push({ type: 'cityCaptured', id: e.id, hex: j, from, to, level: e.level });
    } else {
      removeEntity(state, sid, 'destroyed', events);
    }
  }
  const ce = capEff(state, j);
  if (pop[j] > ce) pop[j] = ce;
  if (from > 0) state.stats.players[to - 1].contestedGains++;
}
